import { formatGBP } from "../../utils/format";

export const DASH = "—";

function pct(value, places = 2) {
  return value === null || value === undefined ? DASH : `${(value * 100).toFixed(places)}%`;
}

function money(value) {
  return value === null || value === undefined ? DASH : formatGBP(value);
}

function money0(value) {
  return value === null || value === undefined
    ? DASH
    : new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        maximumFractionDigits: 0,
      }).format(value);
}

/**
 * Contribution breaks even at a MER of 1.80 and overheads are covered at 2.40
 * — the same thresholds the weekly retro email uses, kept in sync by hand.
 */
export const BREAKEVEN_MER = 1.8;
export const TARGET_MER = 2.4;

/**
 * Columns, grouped the way the question gets asked: how much traffic arrived
 * and how far it got, what it was worth, and what it cost. MER closes the ads
 * group because it is the answer the columns before it explain.
 *
 * With sixteen columns the groups are not decoration — they are what makes the
 * row scannable, and each one gets a dividing rule so the eye can find the
 * boundary without counting.
 */
export const GROUPS = [
  {
    key: "period",
    label: "",
    columns: [{ key: "period", label: "Period", align: "left", locked: true }],
  },
  {
    key: "sessions",
    label: "Session data",
    columns: [
      { key: "sessions", label: "Sessions", render: (r) => r.sessions.toLocaleString() },
      { key: "paidShare", label: "Paid", render: (r) => pct(r.paidShare, 0) },
      { key: "atcRate", label: "ATC", render: (r) => pct(r.atcRate) },
      { key: "atcToCheckout", label: "ATC → checkout", render: (r) => pct(r.atcToCheckout, 1) },
      {
        key: "checkoutToPurchase",
        label: "Checkout completion",
        render: (r) => pct(r.checkoutToPurchase, 1),
      },
      { key: "cvr", label: "CVR", render: (r) => pct(r.cvr) },
    ],
  },
  {
    key: "trading",
    label: "Trading",
    columns: [
      { key: "orders", label: "Orders", render: (r) => r.orders.toLocaleString() },
      { key: "aov", label: "AOV", render: (r) => money0(r.aov) },
      { key: "revenue", label: "Revenue", render: (r) => money0(r.revenue) },
    ],
  },
  {
    key: "ads",
    label: "Ads",
    columns: [
      { key: "spend", label: "Ad spend", render: (r) => money0(r.spend) },
      { key: "cpc", label: "CPC", render: (r) => money(r.cpc) },
      { key: "cpa", label: "CPA", render: (r) => money0(r.cpa) },
      {
        key: "mer",
        label: "MER",
        render: (r) => (r.mer === null || r.mer === undefined ? DASH : `${r.mer.toFixed(2)}×`),
      },
    ],
  },
  {
    key: "products",
    label: "Products",
    columns: [{ key: "products", label: "Top products", align: "left", wrap: true }],
  },
];

export const ALL_COLUMN_KEYS = GROUPS.flatMap((g) => g.columns.map((c) => c.key));

/** Columns the reader is allowed to hide — Period always stays. */
export const HIDEABLE_COLUMN_KEYS = GROUPS.flatMap((g) =>
  g.columns.filter((c) => !c.locked).map((c) => c.key)
);

/**
 * The groups and columns still visible, with empty groups dropped so no
 * dividing rule is left hanging over nothing.
 */
export function visibleGroups(hidden) {
  return GROUPS.map((g) => ({
    ...g,
    columns: g.columns.filter((c) => !hidden.has(c.key)),
  })).filter((g) => g.columns.length > 0);
}

export function merColor(mer, selected) {
  // A selected row is already inverted and obviously the focus, so the traffic
  // light would only fight the dark background. Drop it rather than pick a
  // second palette that has to stay legible on black.
  if (selected) return "";
  if (mer === null || mer === undefined) return "text-collectors-gray";
  if (mer >= TARGET_MER) return "text-green-700";
  if (mer >= BREAKEVEN_MER) return "text-amber-700";
  return "text-red-700";
}

export { pct, money, money0 };
